import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Helper to get current company number from auth token
const getCompanyNo = (): string => {
  const userData = AuthService.getUserDataFromToken();
  if (!userData?.companyNo) {
    throw new Error('No company number available - user not authenticated');
  }
  return userData.companyNo;
};

// DTO types for lookup data
export interface StatusCodeDto {
  statusCode: number;
  statusType: number;
  lng_1: string;
  lng_2?: string;
}

export interface WarehouseDto {
  companyNo: string;
  warehouseID: string;
  warehouseDescr?: string;
}

export interface FilterGroupDto {
  companyNo: string;
  filterGroupID: string;
  filterGroupDescr?: string;
  filterGroupType: number;
}

export interface PackZoneDto {
  packZoneID: string;
  description?: string;
}

export interface PrinterDto {
  id: number;
  name: string;
  description?: string;
  computer?: {
    id: number;
    name: string;
  };
}

// ProfileGroupDto/getProfileGroups fjernet 2026-07-06: profilgrupper er felles kodeverk i
// master-StatusCodes (StatusType 43) og hentes via useStatusOptions(43)/statusCodesService.

export interface ReportDto {
  id: number;
  reportID: number;
  reportName: string;
  description?: string;
}

export interface RuleGroupDto {
  ruleGroupID: number;
  lng1: string;
  lng2?: string;
}

export interface SqlGroupDto {
  sqlGroupID: number;
  lng1: string;
  lng2?: string;
  sqlGroupSyntax?: string;
}

/**
 * Service for fetching lookup data used in dropdowns/selects
 */
export const lookupService = {
  /**
   * Get status codes by status type
   * @param statusType The status type to filter by
   */
  async getStatusCodes(statusType: number): Promise<StatusCodeDto[]> {
    const companyNo = getCompanyNo();
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/statuscodes?statusType=${statusType}`
    );
    if (!response.ok) {
      throw new Error(`Failed to get status codes: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get all warehouses for current company
   */
  async getWarehouses(): Promise<WarehouseDto[]> {
    const companyNo = getCompanyNo();
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/warehouse`
    );
    if (!response.ok) {
      throw new Error(`Failed to get warehouses: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get filter groups for current tenant
   * @param filterGroupType Optional filter group type (e.g., 1 for PList filter groups)
   */
  async getFilterGroups(filterGroupType?: number): Promise<FilterGroupDto[]> {
    let url = `${API_BASE_URL}/filter-groups/current`;
    if (filterGroupType !== undefined) {
      url = `${API_BASE_URL}/filter-groups/current/by-type/${filterGroupType}`;
    }
    const response = await AuthService.authenticatedGet(url);
    if (!response.ok) {
      throw new Error(`Failed to get filter groups: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get pack zones for current company
   */
  async getPackZones(): Promise<PackZoneDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/packing/packzones`
    );
    if (!response.ok) {
      throw new Error(`Failed to get pack zones: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get available printers
   */
  async getPrinters(): Promise<PrinterDto[]> {
    const companyNo = getCompanyNo();
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/printing/printers`
    );
    if (!response.ok) {
      throw new Error(`Failed to get printers: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get reports for current company (used for label/document configuration)
   */
  async getReports(): Promise<ReportDto[]> {
    const companyNo = getCompanyNo();
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration`
    );
    if (!response.ok) {
      throw new Error(`Failed to get reports: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get all rule groups for current tenant
   */
  async getRuleGroups(): Promise<RuleGroupDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/system-rule-groups`
    );
    if (!response.ok) {
      throw new Error(`Failed to get rule groups: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get all SQL groups for current tenant
   */
  async getSqlGroups(): Promise<SqlGroupDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/rule-composition/sql-groups`
    );
    if (!response.ok) {
      throw new Error(`Failed to get SQL groups: ${response.status}`);
    }
    return response.json();
  },

  // Predefined StatusType constants for profile fields
  StatusTypes: {
    CORRECTION_DIRECTION: 6,          // StcTransCorrectionDefault (Ut av lager / Inn på lager)
    ORDER_LINE_SORT_ORDER: 9,         // OrderLineSortOrder
    STAND005_TYPE: 10,                // PLSTAND005Type (Layouttype for batchregistrering)
    APPLY_CHANGES_EVENT: 11,          // PListApplyChangesEvent (Lagre fra app til server)
    PICKLIST_DETAIL_VIEW: 12,         // PickListDetailView (Liste, detalj, listeplukk)
    PICKLIST_VERSION: 13,             // PickListVersion (Plukklistedesign)
    GOODS_RECEPTION_VERSION: 31,      // GoodsReceptionVersion (Varemottakdesign)
    PLIST_AUTO_CALC_MODE: 33,         // PListAutoCalcMode (Kalkulasjon i plukklisteavslutning)
    PROFILE_LOG_LEVEL: 35,            // ProfileLogLevel (Loggnivå i APP)
    // SystemSetup specific status types
    WAREHOUSE_METHOD: 4,              // WarehouseMethod (Lagernivå)
    CONSIGNMENT_SOFTWARE: 7,          // ConsignmentSoftwareID (Eksternt fraktprogram)
    CLOUD_STORAGE_TYPE: 23,           // CloudStorageType (Skylagringstjeneste)
    SMTP_TLS: 29,                     // SMTPUseTLS
    PLIST_SHORTAGE_RULE: 30,          // PListShortageRule (Plukklistebehandling ved manko)
    SYSTEM_LOG_LEVEL: 37,             // SystemLogLevel (Loggnivå)
  } as const
};
